import { sessionService } from '@/providers/services/auth/session';
import {CollectionModel} from '@/models/content/collection';
import { DEFAULT_IMAGES_PATH } from '@/utils/constants';
import { taxonomySerializer } from './taxonomy';
import { questionSerializer } from './question';
import { resourceSerializer } from './resource';
import { findAnyCompetencyTitle, gutCourseNames, truncateString } from '@/utils/utils';
import moment from 'moment';

export class CollectionSerializer {

  static get instance() {
    return this.INSTANCE;
  }
  private static INSTANCE = new CollectionSerializer();

  public serializeCollection(collection: any): CollectionModel {
    const cdnUrls = sessionService.getCdnUrl();
    const contentCdnUrl = cdnUrls.content_cdn_url;
    const thumbnailUrl = collection.thumbnail ?
      contentCdnUrl + collection.thumbnail :
        `${window.location.origin}/` + DEFAULT_IMAGES_PATH.collection;
    const result: CollectionModel = {
      id: collection.id,
      title: collection.title,
      url: collection.url,
      questionCount: collection.questionCount || 0,
      resourceCount: collection.resourceCount || 0,
      description: collection.learningObjective,
      format: collection.format || 'collection',
      efficacy: collection.efficacy,
      relevance: collection.relevance,
      engagement: collection.engagement,
      standards: taxonomySerializer.normalizeTaxonomyObject(collection.taxonomy),
      creator: taxonomySerializer.normalizeCreator(collection.creator),
      thumbnailUrl,
    };
    return result;
  }

  public serializeSignatureCollection(collection: any): CollectionModel {
    const cdnUrls = sessionService.getCdnUrl();
    const contentCdnUrl = cdnUrls.content_cdn_url;
    const thumbnailUrl = collection.thumbnail ?
      contentCdnUrl + collection.thumbnail :
        `${window.location.origin}/` + DEFAULT_IMAGES_PATH.collection;
    const result: CollectionModel = {
      id: collection.id,
      title: collection.title,
      thumbnailUrl,
      format: 'signature-collection',
    };
    return result;
  }


  /**
   * Normalize the Collection data into a Collection object
   * @param payload
   * @returns {Question}
   */
  public normalizeReadCollection(payload: any) {
    const serializer = this;
    const cdnUrls = sessionService.getCdnUrl();
    const basePath = cdnUrls.content_cdn_url;
    const appRootPath = window.location.origin; // configuration appRootPath
    const thumbnailUrl = payload.thumbnail
      ? basePath + payload.thumbnail
      : appRootPath + DEFAULT_IMAGES_PATH.collection;
    const metadata = payload.metadata || {};
    return {
      id:
        payload.target_collection_id ||
        payload.suggested_content_id ||
        payload.id,
      pathId: payload.id,
      title: payload.title,
      learningObjectives: payload.learning_objective,
      isVisibleOnProfile:
        typeof payload.visible_on_profile !== 'undefined'
          ? payload.visible_on_profile
          : true,
      children: serializer.normalizeResources(payload.content),
      questionCount: payload.question_count || 0,
      resourceCount: payload.resource_count || 0,
      sequence: payload.sequence_id,
      thumbnailUrl,
      url: payload.url || null,
      standards: taxonomySerializer.normalizeTaxonomyObject(payload.taxonomy),
      courseId:
        payload.target_course_id ||
        payload.suggested_course_id ||
        payload.course_id,
      unitId:
        payload.target_unit_id || payload.suggested_unit_id || payload.unit_id,
      lessonId:
        payload.target_lesson_id ||
        payload.suggested_lesson_id ||
        payload.lesson_id,
      creatorId: payload.creator_id,
      ownerId: payload.owner_id,
      collectionSubType:
        payload.target_content_subtype || payload.suggested_content_subtype,
      metadata,
      centurySkills:
        metadata['21_century_skills'] && metadata['21_century_skills'].length
          ? metadata['21_century_skills']
          : [],
      format:
        payload.format ||
        payload.target_content_type ||
        payload.suggested_content_type,
    };
  }

  public collectionPulloutSerializer(collection: any) {
    const defaultVectorValue = 0.5;
    let resultSet = {};
    if (collection) {
      resultSet = {
        descriptive: {
          title: collection.title,
          description: truncateString(collection.description),
        },

        creation: {
          'Publisher': 'Gooru Org',
          'Publish Status': collection.isPublished
            ? 'Published'
            : 'Unpublished',
          'Collaborator': collection.collaboratorIDs,
          'Created by': collection.creator.username,
          'Aggregator': collection.aggregator ? collection.aggregator : null,
          'Modified by':
            collection.lastModifiedUser.username || collection.lastModifiedBy,
          'Date Modified':
            moment(collection.lastModified).format('MMMM DD, YYYY') || null,
          'License': collection.license
            ? collection.license.code
            : 'Public Domain',
        },

        educational: {
          'Audience': collection.audience,
          'Grade Level': collection.grade,
          'Learning Objective': collection.learningObjectives,
          'Editorial Range': collection.publisherQualityIndicator,
        },

        media: {
          Keywords: collection.keyPoints,
          Visibility: collection.isVisibleOnProfile ? 'True' : 'False',
        },

        instructional: {
          'Instructional Model': collection.instructionalModel,
          '21st Century Skills': collection.skills,
        },

        framework: {
          subject: collection.taxonomySet.subject,
          course: gutCourseNames(collection.taxonomySet.course),
          domain: collection.taxonomySet.domain,
          standard: collection.standards || [],
        },

        vector: {
          relevance: collection.relevance || defaultVectorValue,
          engagement: collection.engagement || defaultVectorValue,
          efficacy: collection.efficacy || defaultVectorValue,
        },
      };
    }
    return resultSet;
  }

  private normalizeResources(payload: any) {
    if (Array.isArray(payload)) {
      return payload.map((item: any) =>
        item.content_format === 'resource'
          ? resourceSerializer.normalizeReadResource(item)
          : questionSerializer.normalizeReadQuestion(item),
      );
    }
    return [];
  }


}

export const collectionSerializer = CollectionSerializer.instance;
